import { useSearchParams } from "react-router-dom";
import "@/features/auth/templates/styles/style.scss";
import { inviteService } from "@/shared/services/api/invite";
import { useEffect, useState } from "react";
import { IInviteDetailResponse } from "@/shared/services/interface/responses/invite-response";
import { useAuth } from "@/core/context/AuthContext";
import GuestInviteContent from "./guest-invite-content";
import AuthInviteContent from "./auth-invite-content";
import BrokenLinkContent from "./broken-link-content";

export default function WorkspaceInviteView() {
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const shareLinkHash = searchParams.get("sh");
  const inviterName = searchParams.get("inviterName") || "";
  const contextName = searchParams.get("contextName") || "";
  const contextType = searchParams.get("contextType") || "";
  const toEmail = searchParams.get("toEmail") || "";

  // Check if all required parameters are present
  const hasAllRequiredParams =
    shareLinkHash && inviterName && contextName && contextType && toEmail;

  // If any required parameter is missing, show broken link content
  if (!hasAllRequiredParams) {
    return <BrokenLinkContent />;
  }

  const [isLoading, setIsLoading] = useState(false);
  const [inviteDetail, setInviteDetail] =
    useState<IInviteDetailResponse | null>(null);

  // Extract name from email if inviterName is an email
  const getInviterDisplayName = () => {
    if (!inviterName) return "";
    // If it's an email, extract the part before @
    if (inviterName.includes("@")) {
      return inviterName.split("@")[0];
    }
    return inviterName;
  };

  const inviterDisplayName = getInviterDisplayName();

  // Get Turkish suffix based on contextType (same logic as backend)
  const getTurkishSuffix = (type: string): string => {
    if (type === "çalışma alanı") {
      return "na";
    } else if (type === "proje" || type === "şartname") {
      return "ye";
    }
    // Default fallback
    return "na";
  };

  // Build invitation message
  const getInvitationMessage = (): string => {
    if (contextName && contextType) {
      const turkishSuffix = getTurkishSuffix(contextType);
      return `${inviterDisplayName} seni Procright'ta ${contextName} adlı ${contextType}${turkishSuffix} davet etti!`;
    }
    // Fallback message
    return `${inviterDisplayName} seni Procright'ta bir çalışma alanına davet etti!`;
  };

  const invitationMessage = getInvitationMessage();

  const getDetail = async () => {
    if (!shareLinkHash) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const response = await inviteService.getInviteDetail(shareLinkHash);

    if (response.success && response.data) {
      setInviteDetail(response.data);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    // Only make API call if user is authenticated
    if (isAuthenticated) {
      getDetail();
    } else {
      setIsLoading(false);
    }
  }, [shareLinkHash, isAuthenticated]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="auth-page-container flex items-center justify-center w-[395px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // If user is authenticated, check if email matches
  if (isAuthenticated) {
    const apiEmail = inviteDetail?.email;
    // If API email matches toEmail, show auth content
    if (apiEmail && apiEmail === toEmail) {
      return (
        <AuthInviteContent
          invitationMessage={invitationMessage}
          toEmail={toEmail}
          shareLinkHash={shareLinkHash || ""}
          successMessage="Davet başarıyla kabul edildi!"
        />
      );
    }
    // If API call failed or email doesn't match, show guest content
    return (
      <GuestInviteContent
        invitationMessage={invitationMessage}
        toEmail={toEmail}
      />
    );
  }

  // If user is not authenticated, show guest content
  return (
    <GuestInviteContent
      invitationMessage={invitationMessage}
      toEmail={toEmail}
    />
  );
}
